<template>
	<div
		v-if="rewardGridItems && rewardGridItems.length > 0"
		class="rewards-grid"
		:class="`rewards-grid--${rewardGridItems.length > 1 ? 'multiple' : 'single'} rewards-grid--count-${count} rewards-grid--${context} rewards-grid--${template}`"
		:style="{
			gap: `${(dynamicSize?.gap ?? 16) / 16}rem`,
		}"
	>
		<div
			v-for="(upgrade, idx) in rewardGridItems"
			:key="idx"
			class="rewards-grid__item"
		>
			<Tooltip
				v-if="upgrade"
				:heading="translate(upgrade?.item?.heading as string)"
				:disabled="hasUpgradeAggregationDetails"
				customWidth="18rem"
				:placement="Placements.TOP"
			>
				<template #element>
					<div class="rewards-grid__value">
						<UpgradeBox
							:id="upgrade.upgrade_id"
							:heading="upgrade?.item?.heading as string"
							:description="upgrade?.item?.description as string"
							:descriptionReplace="
								upgrade?.item?.descriptionReplace
							"
							:maxLevel="upgrade?.item?.maxLevel"
							:isNew="false"
							:isStarred="upgrade?.item?.isStarred"
							:upgrade-callback="upgrade?.item?.upgradeCallback"
							:isEventType="false"
							:skin="SkinTypes.DARK"
						/>
					</div>
				</template>
				<template #tooltip>
					<span
						class="reward-tooltip"
						v-html="
							descriptionText(
								upgrade?.item?.description as string,
								upgrade?.item?.descriptionReplace as Record<
									string,
									string
								>,
							)
						"
					/>
				</template>
			</Tooltip>
		</div>
	</div>
</template>

<script lang="ts" setup>
import Tooltip from "@/components/Tooltip.vue";
import UpgradeBox from "@/components/Upgrades/UpgradeBox.vue";
import type { UpgradeItem } from "@/game/entities/config/upgrades";
import { upgrades } from "@/game/entities/config/upgrades";
import type { UpgradeSlotItem } from "@/types/shop";
import { OfferTemplate } from "@/types/shop";
import { Placements, RewardsGridContext, SkinTypes } from "@/types/ui";
import { replacePlaceholder } from "@/utils/replacePlaceholder";
import { canTranslate, translate } from "@/utils/translate";
import { computed } from "vue";

interface Props {
	items: UpgradeSlotItem[] | null;
	context: RewardsGridContext;
	template: OfferTemplate;
}

const { items, context, template } = defineProps<Props>();

type RewardGridItem = UpgradeSlotItem & {
	item: UpgradeItem | null;
};

const rewardGridItems = computed((): RewardGridItem[] | null => {
	if (!items) return null;

	return (items as UpgradeSlotItem[]).map(
		(slotItem: UpgradeSlotItem): RewardGridItem => {
			const upgradeData = upgrades.get(
				slotItem.upgrade_id,
			) as UpgradeItem;

			return {
				...slotItem,
				item: upgradeData ?? null,
			};
		},
	);
});

const count = computed((): number => rewardGridItems.value?.length ?? 0);

const hasUpgradeAggregationDetails = computed((): boolean => {
	if (!rewardGridItems.value || rewardGridItems.value.length < 1)
		return false;
	if (
		context === RewardsGridContext.POPUP &&
		template === OfferTemplate.SLOTS_2 &&
		rewardGridItems.value.length === 1
	)
		return true;
	return false;
});

const dynamicSize = computed((): Record<string, number> => {
	// NOTE GD: Max 2 upgrades
	if (context === RewardsGridContext.POPUP) {
		switch (count.value) {
			case 1:
				return { gap: 0 };
			case 2:
				return { gap: 12 };
			default:
				return { gap: 12 };
		}
	} else {
		switch (count.value) {
			// TODO shop tile gfx
			default:
				return { icon: 80 };
		}
	}
});

const descriptionText = (
	description: string,
	descriptionReplace: Record<string, string>,
): string => {
	let text = translate(description);
	Object.entries(descriptionReplace).forEach(
		([key, value]: [string, string]): void => {
			const textTranslated = canTranslate(value)
				? translate(value)
				: value;
			text = replacePlaceholder(text, key, textTranslated);
		},
	);
	return text;
};
</script>

<style lang="scss" scoped>
.rewards-grid {
	display: grid;
	position: relative;
	grid-template-columns: repeat(3, 1fr);
	align-items: center;
	justify-items: center;
	z-index: 1;
	padding: 1rem 0.625rem 0.25rem;

	&::before {
		position: absolute;
		top: 0;
		left: 50%;
		width: 140%;
		height: 140%;
		content: "";
		@include background(
			url($path-images + "shop/backgrounds/radial-glow-xl.avif"),
			contain,
			center,
			no-repeat
		);
		transform: translate(-50%, -50%);
		z-index: -1;
		animation: rotateInfinite 20s linear infinite;
		pointer-events: none;
	}

	&--single,
	&--multiple {
		:deep(.upgrade-box) {
			@include unclickable-element;
			height: 100%;
		}
	}

	&--single {
		:deep(.upgrade-box-container .upgrade-box__content) {
			align-items: center;
		}
	}

	&--multiple {
		:deep(.upgrade-box-container) {
			.upgrade-box__heading .upgrade-box__heading__wrapper {
				justify-content: center;
			}

			.upgrade-box__content .description {
				display: none;
			}
		}
	}

	&--slots_3,
	&--slots_4 {
		&::before {
			display: none;
		}
	}

	.price {
		:deep(.shrink-text) {
			color: #fff3dd;
		}
	}

	&__item {
		@include flex-col-center;
		row-gap: 6px;
		justify-items: center;
		width: 100%;
		height: 100%;

		.v-popper {
			width: 100%;
			height: 100%;
		}
	}

	&__value {
		position: relative;
		width: 100%;
		height: 100%;

		&-item {
			&--horizontal {
				@include flex-row-center;
			}

			&--vertical {
				@include flex-col-center;
			}
		}
	}

	/* 1 item: center */
	&--count-1 {
		grid-template-columns: 1fr;
		gap: 0.75rem;

		&.rewards-grid--card {
			column-gap: 0.25rem;
			row-gap: 0.25rem;
			padding: 0.5rem 2.5rem;
		}
	}

	/* 2 items: 2 columns */
	&--count-2 {
		grid-template-columns: repeat(2, 1fr);

		&.rewards-grid--card {
			column-gap: 0.25rem;
			row-gap: 0.25rem;
			padding: 0.375rem;
		}
	}
}
</style>
